import type { PromptChallengeGame } from '@/types/promptChallenge';

/**
 * Dados mock do jogo Prompt Challenge
 * Em produção, virão do backend via API
 */
export const mockPromptChallengeGame: PromptChallengeGame = {
  id: 'game-2',
  title: 'Prompt Challenge',
  totalXP: 50, // 10 XP por desafio
  challenges: [
    {
      id: 'challenge-1',
      number: 1,
      title: 'Guia Roma',
      objective: {
        title: 'Crie um prompt para um assistente de viagens',
        description: 'Monte um prompt estruturado para criar um roteiro de viagem em Roma',
        difficulty: 'Intermediário',
      },
      availableBlocks: [
        {
          id: 'block-1',
          type: 'persona',
          content: 'Atue como um guia turístico experiente',
        },
        {
          id: 'block-2',
          type: 'context',
          content: 'Planeje um roteiro de 3 dias em Roma',
        },
        {
          id: 'block-3',
          type: 'task',
          content: 'Crie uma tabela com horários sugeridos',
        },
        {
          id: 'block-4',
          type: 'restrictions',
          content: 'Foque em atrações gastronômicas locais',
        },
        {
          id: 'block-5',
          type: 'restrictions',
          content: 'Não inclua voos internacionais no roteiro',
        },
        {
          id: 'block-6',
          type: 'task',
          content: 'Sugira apenas monumentos históricos',
        },
        {
          id: 'block-7',
          type: 'restrictions',
          content: 'Seja amigável e conciso',
        },
      ],
      correctSolution: {
        persona: 'block-1',
        context: 'block-2',
        task: 'block-3',
        restrictions: 'block-4',
      },
      hint: 'A persona deve vir sempre primeiro para definir o tom.',
      timeLimit: 180, // 3 minutos
      isLocked: false,
    },
    {
      id: 'challenge-2',
      number: 2,
      title: 'Suporte Técnico',
      objective: {
        title: 'Crie um prompt para atendimento ao cliente',
        description: 'Monte um prompt para um assistente de suporte técnico',
        difficulty: 'Intermediário',
      },
      availableBlocks: [
        {
          id: 'block-8',
          type: 'persona',
          content: 'Atue como um especialista em suporte técnico',
        },
        {
          id: 'block-9',
          type: 'context',
          content: 'Resolva problemas de conexão de internet',
        },
        {
          id: 'block-10',
          type: 'task',
          content: 'Forneça um guia passo a passo para diagnóstico',
        },
        {
          id: 'block-11',
          type: 'restrictions',
          content: 'Use linguagem simples e acessível',
        },
        {
          id: 'block-12',
          type: 'restrictions',
          content: 'Não use termos técnicos complexos',
        },
        {
          id: 'block-13',
          type: 'task',
          content: 'Explique em formato de lista numerada',
        },
      ],
      correctSolution: {
        persona: 'block-8',
        context: 'block-9',
        task: 'block-10',
        restrictions: 'block-11',
      },
      hint: 'Priorize a clareza da comunicação nas restrições.',
      timeLimit: 180,
      isLocked: true,
    },
    {
      id: 'challenge-3',
      number: 3,
      title: 'Redator Criativo',
      objective: {
        title: 'Crie um prompt para criação de conteúdo',
        description: 'Monte um prompt para um assistente de redação criativa',
        difficulty: 'Avançado',
      },
      availableBlocks: [
        {
          id: 'block-14',
          type: 'persona',
          content: 'Atue como um redator criativo premiado',
        },
        {
          id: 'block-15',
          type: 'context',
          content: 'Escreva uma campanha publicitária para startup de tecnologia',
        },
        {
          id: 'block-16',
          type: 'task',
          content: 'Crie 3 slogans impactantes e 1 parágrafo descritivo',
        },
        {
          id: 'block-17',
          type: 'restrictions',
          content: 'Use tom inspirador e inovador',
        },
        {
          id: 'block-18',
          type: 'restrictions',
          content: 'Foque no público jovem (18-35 anos)',
        },
        {
          id: 'block-19',
          type: 'task',
          content: 'Inclua hashtags relevantes para redes sociais',
        },
      ],
      correctSolution: {
        persona: 'block-14',
        context: 'block-15',
        task: 'block-16',
        restrictions: 'block-17',
      },
      hint: 'Defina bem o público-alvo nas restrições.',
      timeLimit: 240, // 4 minutos
      isLocked: true,
    },
    {
      id: 'challenge-4',
      number: 4,
      title: 'Análise de Dados',
      objective: {
        title: 'Crie um prompt para análise analítica',
        description: 'Monte um prompt para um assistente de análise de dados',
        difficulty: 'Avançado',
      },
      availableBlocks: [
        {
          id: 'block-20',
          type: 'persona',
          content: 'Atue como um cientista de dados experiente',
        },
        {
          id: 'block-21',
          type: 'context',
          content: 'Analise dados de vendas do último trimestre',
        },
        {
          id: 'block-22',
          type: 'task',
          content: 'Identifique tendências e crie visualizações',
        },
        {
          id: 'block-23',
          type: 'restrictions',
          content: 'Use metodologia estatística robusta',
        },
        {
          id: 'block-24',
          type: 'restrictions',
          content: 'Apresente insights acionáveis',
        },
      ],
      correctSolution: {
        persona: 'block-20',
        context: 'block-21',
        task: 'block-22',
        restrictions: 'block-23',
      },
      hint: 'Metodologia clara é essencial em análise de dados.',
      timeLimit: 240,
      isLocked: true,
    },
    {
      id: 'challenge-5',
      number: 5,
      title: 'Educação Infantil',
      objective: {
        title: 'Crie um prompt para conteúdo educacional',
        description: 'Monte um prompt para um assistente educacional para crianças',
        difficulty: 'Intermediário',
      },
      availableBlocks: [
        {
          id: 'block-25',
          type: 'persona',
          content: 'Atue como um professor de ensino fundamental',
        },
        {
          id: 'block-26',
          type: 'context',
          content: 'Explique o conceito de fotossíntese',
        },
        {
          id: 'block-27',
          type: 'task',
          content: 'Use analogias e exemplos do cotidiano',
        },
        {
          id: 'block-28',
          type: 'restrictions',
          content: 'Linguagem adequada para crianças de 8-10 anos',
        },
        {
          id: 'block-29',
          type: 'restrictions',
          content: 'Inclua perguntas interativas',
        },
      ],
      correctSolution: {
        persona: 'block-25',
        context: 'block-26',
        task: 'block-27',
        restrictions: 'block-28',
      },
      hint: 'Adapte a linguagem ao público-alvo.',
      timeLimit: 180,
      isLocked: true,
    },
  ],
};
